import React, {
    createContext,
    FC,
    ReactNode,
    useContext,
    useEffect,
    useState
} from "react";
import {Employee} from "../services/employee";
import usePersistState from "../hooks/usePresistState.tsx";

// Define types for the authentication context
interface AuthContextType {
    employeeI: Employee|undefined;
    authReady: boolean;
    isAuthenticated: boolean;
    logoutLoading: boolean;
    setLogoutLoading: (bool: boolean) => void;
    setEmp: (emp: Employee|undefined) => void;
    addSignature: (signature: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [employeeI, setEmployee] = usePersistState<Employee|undefined>(
        undefined,
        "auth-user"
    );
    const [authReady, setAuthReady] = useState<boolean>(false);

    useEffect(() => {
        setAuthReady(true);
    }, []);

    const sanitizeEmployee = (emp: Employee | undefined): Employee | undefined => {
        if (!emp) {
            return undefined;
        }

        return {
            ...emp,
            password: undefined,
        };
    };

    const setEmp = (emp:Employee|undefined) => {
        setEmployee(sanitizeEmployee(emp));
    }
    
    const [logoutLoading, setLogout] = useState<boolean>(
        false
    );

    const setLogoutLoading = (bool:boolean) => {
        setLogout(bool);
    }

    const addSignature = (signature: string) => {
        if (employeeI) {
            setEmployee({
                ...employeeI,
                signature,
            });
        }
    }

    const isAuthenticated = Boolean(employeeI);


    // Provide the authentication context to the children components
    return (
        <AuthContext.Provider
            value={{
                employeeI,
                authReady,
                isAuthenticated,
                setEmp,
                addSignature,
                logoutLoading,
                setLogoutLoading
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export default AuthProvider;
